'use client';

import { useEffect, useState } from 'react';

/**
 * Picks a random message from a list and rotates to a new random one at a fixed interval.
 * Consecutive picks avoid immediate repeats when more than one message is available.
 */
export function useFunMessage(
  messages: string[],
  active: boolean,
  intervalMs = 6000,
): string {
  // Start deterministically at 0 so SSR and first client render agree —
  // Math.random() in the initial state caused a hydration mismatch. Pick a
  // random starting index after mount (client-only) to keep messages varied
  // across sessions.
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (messages.length > 1) {
      setIndex(Math.floor(Math.random() * messages.length));
    }
  }, [messages.length]);

  useEffect(() => {
    if (!active || messages.length <= 1) return;
    const interval = setInterval(() => {
      setIndex((prev) => {
        if (messages.length <= 1) return prev;
        // Pick a different random index by sampling from (len - 1) and shifting past prev
        let next = Math.floor(Math.random() * (messages.length - 1));
        if (next >= prev) next += 1;
        return next;
      });
    }, intervalMs);
    return () => clearInterval(interval);
  }, [active, messages.length, intervalMs]);

  return messages[index % messages.length] ?? '';
}
